import {
  Card,
  CardBody,
  CardHeader,
  chakra,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  ListItem,
  Stack,
  StackDivider,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { useGlobalContext } from 'GlobalContext';
import moment from 'moment';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useState } from 'react';
import { FaLock } from 'react-icons/fa';
import {
  FaCircle,
  FaComputer,
  FaRegCircleUser,
  FaUnlock,
} from 'react-icons/fa6';
import { FiEdit } from 'react-icons/fi';
import { IoCopyOutline } from 'react-icons/io5';
import { MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { blockApp, temporaryUnlock, trustUnlock } from 'services/app';
import { Mixpanel } from 'services/mixpanel';
import ProtectedButton from 'shared/components/ProtectedButton';
import { copyToClipboard } from 'shared/helpers/clipboard';
import { MAX_CONSECUTIVE_TRUST_UNLOCKS } from 'shared/models/app';
import { useAppTabContext } from '../AppContext';

const AppDetails = () => {
  const { confirm } = useGlobalContext();
  const { loadApps, customer } = useCustomerContext();
  const { app, openEditAppDetailsModal } = useAppTabContext();
  const [loadingUnlock, setLoadingUnlock] = useState(false);
  const [loadingBlock, setLoadingBlock] = useState(false);
  const { openAppStatusHistoryModal } = useAppTabContext();

  const copyIDI = () => {
    Mixpanel.track('customers-app-details-clicked-copy-idi');
    copyToClipboard(app.idi, 'IDI Copiado');
  };

  const onClickTrustUnlock = () => {
    confirm(
      'Deseja desbloquer o app?',
      `Esta ação só pode ser executada uma vez ao mês. Desbloqueios consecutivos: ${app.consecutiveBillingUnlocks} de ${MAX_CONSECUTIVE_TRUST_UNLOCKS}`,
      () => {
        Mixpanel.track('customers-app-details-clicked-unlock');
        setLoadingUnlock(true);
        trustUnlock(app.idi, customer.id, app.canResetTrustUnlocks)
          .then(() => {
            toast.success('App desbloqueado');
            loadApps();
          })
          .catch((e) => {
            toast.error(e?.response?.data?.message ?? 'Falha ao desbloquear');
          })
          .finally(() => setLoadingUnlock(false));
      },
    );
  };

  const onClickTemporaryUnlock = () => {
    confirm(
      'Deseja desbloquer o app?',
      'O app será desbloqueado até a próxima automação',
      () => {
        Mixpanel.track('customers-app-details-clicked-unlock');
        setLoadingUnlock(true);
        temporaryUnlock(app.idi, customer.id)
          .then(() => {
            toast.success('App desbloqueado');
            loadApps();
          })
          .catch((e) => {
            const err = e?.response?.data?.message.includes(
              'temporarily unlocked in the last 30 days',
            )
              ? ': Desbloqueio recente (30 dias)'
              : '';
            toast.error(`Falha ao desbloquear${err}`);
          })
          .finally(() => setLoadingUnlock(false));
      },
    );
  };

  const onClickBlock = () => {
    confirm(`Bloquear o App ${app.idi}`, 'Deseja continuar?', () => {
      Mixpanel.track('customers-app-details-clicked-lock');
      setLoadingBlock(true);
      blockApp(app.idi, customer.id)
        .then(() => {
          toast.success('App bloqueado');
          loadApps();
        })
        .catch(() => {
          toast.error('Falha ao bloquear App');
        })
        .finally(() => setLoadingBlock(false));
    });
  };

  return (
    <Card>
      <CardHeader>
        <Flex
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '1rem', md: '0.5rem' }}
        >
          <HStack align="center" wrap="wrap" gap="1rem">
            <HStack align="center">
              <Icon as={FaComputer} />
              <Heading size="sm">{app.nickname}</Heading>
            </HStack>
            <Tag variant="outline" rounded="full" color="yooga.400">
              <TagLeftIcon as={FaRegCircleUser} />
              <TagLabel>
                IDI APP: <chakra.span color="gray.500">{app.idi}</chakra.span>
              </TagLabel>
              <IconButton
                ml="0.5rem"
                aria-label="Copiar IDI"
                size="xs"
                variant="link"
                icon={<IoCopyOutline />}
                onClick={copyIDI}
              />
            </Tag>
          </HStack>

          <HStack wrap={{ base: 'wrap', md: 'nowrap' }}>
            <ProtectedButton
              permission="installation-card-details-update"
              leftIcon={<FiEdit />}
              variant="ghost"
              size="sm"
              py="1.5rem"
              onClick={openEditAppDetailsModal}
            >
              Editar
            </ProtectedButton>

            <ProtectedButton
              permission="installation-card-details-block-update"
              leftIcon={<FaLock />}
              variant="filled"
              bg="red.500"
              size="sm"
              py="1.5rem"
              isLoading={loadingBlock}
              isDisabled={
                app.billingStatusV2 === 'BLOQUEADO' ||
                app.billingStatusV2 === 'BLOQUEADO_TESTE'
              }
              onClick={onClickBlock}
            >
              Bloquear
            </ProtectedButton>

            {app.billingUnlock == null ||
            moment().isAfter(app.billingUnlock) ? (
              <Tooltip
                label={`Instalação já desbloqueada ${MAX_CONSECUTIVE_TRUST_UNLOCKS} vezes consecutivas, necessário aguardar 2 meses para desbloqueio`}
                isDisabled={app.canTrustUnlock}
              >
                <span>
                  <ProtectedButton
                    permission="installation-card-details-unlock-update"
                    leftIcon={<FaUnlock />}
                    variant="filled"
                    size="sm"
                    py="1.5rem"
                    isDisabled={
                      (app.billingStatusV2 !== 'BLOQUEADO' &&
                        app.billingStatusV2 !== 'BLOQUEADO_TESTE') ||
                      !app.canTrustUnlock
                    }
                    isLoading={loadingUnlock}
                    onClick={onClickTrustUnlock}
                  >
                    Desbloqueio de confiança
                  </ProtectedButton>
                </span>
              </Tooltip>
            ) : (
              <ProtectedButton
                permission="installation-card-details-unlock-update"
                leftIcon={<FaUnlock />}
                variant="filled"
                size="sm"
                py="1.5rem"
                isDisabled={
                  app.billingStatusV2 !== 'BLOQUEADO' &&
                  app.billingStatusV2 !== 'BLOQUEADO_TESTE'
                }
                isLoading={loadingUnlock}
                onClick={onClickTemporaryUnlock}
              >
                Desbloqueio temporário
              </ProtectedButton>
            )}
          </HStack>
        </Flex>
      </CardHeader>

      <CardBody>
        <Flex align="center" w="100%">
          <Stack flex="1" align="flex-start">
            <Text variant="label">Status financeiro do sistema</Text>
            <Tag
              size="sm"
              rounded="full"
              color={
                app.billingStatusPriority === 'LOW'
                  ? 'green.400'
                  : app.billingStatusPriority === 'MEDIUM'
                    ? 'yellow.400'
                    : app.billingStatusPriority === 'HIGH'
                      ? 'orange.400'
                      : 'red.400'
              }
            >
              <TagLeftIcon as={FaCircle} />
              <TagLabel fontWeight="600">{app.billingStatusLabel}</TagLabel>
            </Tag>
          </Stack>

          <ProtectedButton
            permission="installation-card-details-update"
            leftIcon={<FiEdit />}
            variant="ghost"
            size="sm"
            py="1.5rem"
            onClick={openAppStatusHistoryModal}
          >
            Histórico da automação
          </ProtectedButton>
        </Flex>
        <Divider my="1rem" />

        <HStack divider={<StackDivider />} spacing="4" wrap="wrap">
          <Stack flex="1" align="flex-start">
            <HStack>
              <Text variant="label">Último desbloqueio de confiança</Text>
              <Tooltip
                label={`Desbloqueia o App e ignora a próxima automação desta semana (ter–sex, 13h; feriados nacionais não contam). Se não houver nenhuma automação restante nesta semana, o App será bloqueado na próxima automação disponível`}
              >
                <HStack>
                  <Icon aria-label="Info" as={MdInfoOutline} />
                </HStack>
              </Tooltip>
            </HStack>
            <HStack>
              <Text fontSize="0.8rem" fontWeight="300">
                {app.billingUnlock
                  ? app.lastDateTrustUnlock.format('DD/MM/YYYY')
                  : 'Nunca'}
              </Text>
              <Text fontSize="0.8rem" fontWeight="300">
                {app.consecutiveBillingUnlocks} de{' '}
                {MAX_CONSECUTIVE_TRUST_UNLOCKS}
              </Text>
            </HStack>
          </Stack>

          <Stack flex="1" align="flex-start">
            <HStack>
              <Text variant="label">Último desbloqueio temporário</Text>
              <Tooltip label="Desbloqueia o App até a próxima automação de bloqueio (ter–sex, 13h; feriados nacionais não contam)">
                <HStack>
                  <Icon aria-label="Info" as={MdInfoOutline} />
                </HStack>
              </Tooltip>
            </HStack>
            <HStack>
              <Text fontSize="0.8rem" fontWeight="300">
                {customer.temporaryUnlockAt
                  ? customer.lastDateTemporaryUnlock.format('DD/MM/YYYY')
                  : 'Nunca'}
              </Text>
            </HStack>
          </Stack>

          <Stack flex="1" align="flex-start">
            <Text variant="label">Fuso horário</Text>
            <Text fontSize="0.8rem" fontWeight="300">
              {app.timezone}
            </Text>
          </Stack>
        </HStack>

        <Divider my="1rem" />

        <HStack divider={<StackDivider />} spacing="4" wrap="wrap">
          <Stack flex="1" align="flex-start">
            <Text variant="label">Ponto de venda</Text>
            <Text fontSize="0.8rem" fontWeight="300">
              {app.tableModule ? 'Mesas' : 'Balcão'}
            </Text>
          </Stack>

          <Stack flex="1" align="flex-start">
            <HStack>
              <Text variant="label">Onboarding habilitado</Text>
              <Tooltip label="Indica se o cliente está em processo de configuração inicial do sistema. Nesse estado, o usuário não consegue usar o App e é redirecionado para a página de onboarding">
                <HStack>
                  <Icon aria-label="Info" as={MdInfoOutline} />
                </HStack>
              </Tooltip>
            </HStack>
            <Text fontSize="0.8rem" fontWeight="300">
              {app.isPendingOnboarding ? 'Sim' : 'Não'}
            </Text>
          </Stack>

          {(!!app.botModule || !!app.emissorModule || !!app.fiscal) && (
            <Stack flex="1" align="flex-start">
              <Text variant="label">Módulos</Text>
              <UnorderedList>
                {!!app.botModule && (
                  <ListItem fontSize="0.8rem" fontWeight="300">
                    ChatBot
                  </ListItem>
                )}
                {!!app.emissorModule && (
                  <ListItem fontSize="0.8rem" fontWeight="300">
                    Emissor
                  </ListItem>
                )}

                {!!app.fiscal && (
                  <ListItem fontSize="0.8rem" fontWeight="300">
                    Fiscal
                  </ListItem>
                )}
              </UnorderedList>
            </Stack>
          )}
        </HStack>
      </CardBody>
    </Card>
  );
};

export default AppDetails;
